import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import {
  PageHeaderWrapper,
  Text,
  Title,
  type UseModalState,
  useModalState,
} from "@src/appV2/redesign/system/ui-components";
import { type WorkplaceQuizQuestion } from "@src/appV2/WorkplaceQuiz/types";
import { useState } from "react";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { SelectableBox } from "../../components/SelectableBox";
import { QuizDialogHeader } from "./QuizDialogHeader";
import { QuizFinishedBottomSheet } from "./QuizFinishedBottomSheet";

export interface QuizQuestionDialogProps {
  modalState: UseModalState;
  quizQuestions: WorkplaceQuizQuestion[];
  isLoading: boolean;
  isError: boolean;
  userPassedQuiz: boolean;
  stillMissingRequirements: boolean;
  isBundle?: boolean;
  onNavigateBack: () => void;
  onQuizSubmit: (chosenOptions: Record<string, string>) => Promise<void>;
  onCloseAfterSubmit: () => void;
  onRetakeQuiz: () => void;
  onBookShift: () => void;
}

export function QuizQuestionDialog(props: QuizQuestionDialogProps) {
  const {
    modalState,
    quizQuestions,
    isLoading,
    isError,
    stillMissingRequirements,
    userPassedQuiz,
    isBundle = false,
    onNavigateBack,
    onQuizSubmit,
    onCloseAfterSubmit,
    onRetakeQuiz,
    onBookShift,
  } = props;

  const quizFinishedModalState = useModalState();
  const theme = useTheme();
  const totalQuestions = quizQuestions.length;
  const [answeredQuestions, setAnsweredQuestions] = useState(0);
  const currentQuestion = quizQuestions[answeredQuestions];
  const [chosenOptionId, setChosenOptionId] = useState<string | undefined>(undefined);
  const [chosenOptions, setChosenOptions] = useState<Record<string, string>>({});
  const [hasValidatedAnswer, setHasValidatedAnswer] = useState(false);
  const lastQuestion = isDefined(totalQuestions) ? answeredQuestions >= totalQuestions - 1 : false;

  if (!currentQuestion) {
    return null;
  }

  function getButtonText() {
    const hasIncorrectAnswer =
      hasValidatedAnswer &&
      chosenOptionId &&
      !currentQuestion.options.find((opt) => opt.id === chosenOptionId)?.isCorrect;

    if (hasIncorrectAnswer) {
      return "Try Again";
    }

    if (lastQuestion) {
      return "Finish Quiz";
    }

    return "Next Question";
  }

  return (
    <>
      <FullScreenDialog skipTopSafeAreaPadding modalState={modalState} variant="tertiary">
        <PageHeaderWrapper variant="tertiary">
          <QuizDialogHeader navigateBack={onNavigateBack} />
        </PageHeaderWrapper>
        <DialogContent>
          <Stack sx={{ height: "100%", justifyContent: "space-between" }}>
            <Stack spacing={10}>
              <Text>
                Question {answeredQuestions + 1} of {totalQuestions}
              </Text>
              <Title component="h4">{currentQuestion.text}</Title>
              <Stack spacing={5}>
                {currentQuestion.options.map((option) => {
                  const isChosen = option.id === chosenOptionId;
                  const shouldShowCorrectAnswer =
                    hasValidatedAnswer &&
                    chosenOptionId &&
                    !currentQuestion.options.find((opt) => opt.id === chosenOptionId)?.isCorrect &&
                    option.isCorrect;

                  function getVariant() {
                    if (!hasValidatedAnswer) {
                      return "primary";
                    }

                    if (shouldShowCorrectAnswer) {
                      return "success";
                    }

                    if (isChosen) {
                      return option.isCorrect ? "success" : "destructive";
                    }

                    return "primary";
                  }

                  return (
                    <SelectableBox
                      key={option.id}
                      isSelected={Boolean(isChosen || shouldShowCorrectAnswer)}
                      variant={getVariant()}
                      disabled={hasValidatedAnswer && !isChosen && !shouldShowCorrectAnswer}
                      sx={{
                        borderColor: theme.palette.border?.primary,
                      }}
                      onToggleSelect={(isSelected: boolean) => {
                        if (hasValidatedAnswer) {
                          return;
                        }

                        setChosenOptionId(isSelected ? option.id : undefined);
                        setHasValidatedAnswer(isSelected);
                      }}
                    >
                      {option.text}
                    </SelectableBox>
                  );
                })}
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            disabled={!isDefined(chosenOptionId)}
            onClick={async () => {
              if (!chosenOptionId) {
                return;
              }

              const selectedOption = currentQuestion.options.find(
                (opt) => opt.id === chosenOptionId
              );

              if (!selectedOption?.isCorrect) {
                onRetakeQuiz();
                return;
              }

              const nextChosenOptions = {
                ...chosenOptions,
                [currentQuestion.id]: chosenOptionId,
              };

              setChosenOptions(nextChosenOptions);
              if (lastQuestion) {
                await onQuizSubmit(nextChosenOptions);
                quizFinishedModalState.openModal();
              } else {
                setChosenOptionId(undefined);
                setHasValidatedAnswer(false);
                setAnsweredQuestions((previous) => previous + 1);
              }
            }}
          >
            {getButtonText()}
          </Button>
        </DialogFooter>
      </FullScreenDialog>
      <QuizFinishedBottomSheet
        modalState={quizFinishedModalState}
        isLoading={isLoading}
        isError={isError}
        userPassedQuiz={userPassedQuiz}
        stillMissingRequirements={stillMissingRequirements}
        isBundle={isBundle}
        onClose={onCloseAfterSubmit}
        onRetakeQuiz={onRetakeQuiz}
        onBookShift={onBookShift}
      />
    </>
  );
}
